/**
 * Shared TypeScript types used across the app.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

export type Profile = 'driving-car' | 'cycling-regular' | 'foot-walking';

export const PROFILE_LABEL: Record<Profile, string> = {
  'driving-car': 'autó',
  'cycling-regular': 'bicikli',
  'foot-walking': 'gyalog',
};

/** ORS directions preference. */
export type RoutePreference = 'recommended' | 'fastest' | 'shortest';

/** ORS `options.avoid_features` entries we expose in the UI. */
export type AvoidFeature = 'highways' | 'ferries' | 'tollways';

export interface RouteOptions {
  preference: RoutePreference;
  avoid: AvoidFeature[];
}

export const DEFAULT_ROUTE_OPTIONS: RouteOptions = {
  preference: 'recommended',
  avoid: [],
};

/**
 * UI language. ORS turn-by-turn instructions are English-only (no HU),
 * so the app ships in English. See `src/i18n.ts` for `t()`.
 */
export type Language = 'hu' | 'en';

export const LANG_DEFAULT: Language = 'en';
export const LANG_SHORT_LABEL: Record<Language, string> = {
  hu: 'HU',
  en: 'EN',
};

/**
 * Way the navigation view frames the user. Three-way cycle on the HUD
 * button; persisted to localStorage.
 *
 * - 'north': flat north-up (pitch=0, bearing=0).
 * - '2d': flat heading-up (pitch=0, bearing tracks GPS).
 * - 'tilted': perspective (pitch=60°, bearing tracks GPS).
 */
export type ViewMode = 'north' | '2d' | 'tilted';

export const VIEW_MODE_CYCLE: readonly ViewMode[] = ['north', '2d', 'tilted'];

export const VIEW_MODE_LABELS: Record<ViewMode, { icon: string; label: string; hint: string }> = {
  north: { icon: '↑', label: 'North', hint: 'Map north up, you centred' },
  '2d': { icon: '⇧', label: '2D', hint: 'Flat map, rotate with direction' },
  tilted: { icon: '⛰', label: 'Tilted', hint: 'Tilted, rotate with direction' },
};

// -------------------------------------------------------------------------
// Pristine-line geographic POIs (speed cameras + speed limits)
// -------------------------------------------------------------------------
//
// Data source: OpenStreetMap via the Overpass API, proxied through
// `/v2/overpass` in the Cloudflare Pages Function. See `src/poi.ts`.

export interface Radar {
  lat: number;
  lng: number;
  /** Camera-enforced speed if OSM has `maxspeed=` on the node itself. */
  maxspeed?: number;
}

export interface SpeedLimit {
  kmh: number;
  lat: number;
  lng: number;
}

/**
 * A roadwork / closure feature extracted from OSM. Source tags:
 *   - `highway=construction` — road physically under construction. Often
 *     tagged with `construction=*` describing what the future road looks
 *     like (e.g. `construction=primary`). We surface this as 'construction'.
 *   - `highway=closed` — explicitly closed (rare in the wild but the OSM
 *     canonical tag). → 'closure'.
 *   - `temporary:access=no` — OSM community convention for temporary
 *     closures, e.g. roadworks. Mapped onto 'closure' so the chip uses
 *     the same red colour as `highway=closed`.
 *
 * Centroid lat/lon is Overpass's `out center;` centroid — fine for an
 * "ahead of me" alert, NOT suitable for measuring the actual length of
 * the roadwork.
 */
export type RoadworkKind = 'construction' | 'closure';

export interface Roadwork {
  kind: RoadworkKind;
  lat: number;
  lng: number;
  /** Future road's highway class when present (`construction=primary` etc.). */
  futureClass?: string;
}

/** Subset of Overpass `out:json` response we consume. */
export interface OsmPoiResponse {
  version: number;
  elements: Array<
    | {
        type: 'node';
        id: number;
        lat: number;
        lon: number;
        tags?: { highway?: string; maxspeed?: string; direction?: string };
      }
    | {
        type: 'way';
        id: number;
        center?: { lat: number; lon: number };
        geometry?: Array<{ lat: number; lon: number }>;
        tags?: {
          maxspeed?: string;
          'maxspeed:forward'?: string;
          'maxspeed:backward'?: string;
          highway?: string;
          construction?: string;
          name?: string;
          'temporary:access'?: string;
        };
      }
  >;
}

/**
 * Subset of OpenRouteService geocoding/search response we consume.
 * See https://openrouteservice.org/dev/#/api-docs/v2/geocode/search
 */
export interface OrsGeocodeFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [longitude, latitude]
  };
  properties: {
    label?: string;
    name?: string;
    layer?: string;
    country?: string;
    region?: string;
    county?: string;
    locality?: string;
    localadmin?: string;
    borough?: string;
    neighbourhood?: string;
    street?: string;
    housenumber?: string;
    postal_code?: string;
  };
}

export interface OrsGeocodeResponse {
  features: OrsGeocodeFeature[];
}

/**
 * Subset of OpenRouteService directions response we consume.
 * Coordinates are returned in [lng, lat] order.
 * See https://openrouteservice.org/dev/#/api-docs/v2/directions/{profile}/json
 */
export interface OrsDirectionsResponse {
  features: Array<{
    type: 'Feature';
    geometry: {
      type: 'LineString';
      coordinates: Array<[number, number]>; // [lng, lat]
    };
    properties: {
      segments: Array<{
        steps: Array<{
          instruction?: string;
          name?: string;
          distance: number; // metres
          duration: number; // seconds
          way_points: [number, number]; // indices into geometry.coordinates
          type?: number;
          /** Roundabout exit index when ORS provides it. */
          exit_number?: number;
          /** Present when request has roundabout_exits: true. */
          exit_bearings?: number[];
          /** Present when request has maneuvers: true. */
          maneuver?: {
            location?: [number, number];
            bearing_before?: number;
            bearing_after?: number;
          };
        }>;
      }>;
      summary: {
        distance: number; // metres
        duration: number; // seconds
      };
    };
  }>;
}

export interface RouteResult {
  coords: LatLng[]; // polyline, ordered start -> end
  distance: number; // metres
  duration: number; // seconds
  steps: Array<{
    instruction: string;
    name: string;
    distance: number;
    duration: number;
    /** Polyline index range this step covers — used by the HUD to highlight
     *  the active turn-by-turn step. Optional because some ORS responses
     *  omit it (we degrade gracefully). */
    way_points?: [number, number];
    /** ORS maneuver type (0–14). See openrouteservice instruction-types. */
    type?: number;
    /** Roundabout exit index when present. */
    exit_number?: number;
    /** Roundabout entrance + passed exit bearings (deg), if requested. */
    exit_bearings?: number[];
    /** Maneuver point + approach/leave bearings, if requested. */
    bearing_before?: number;
    bearing_after?: number;
    maneuver_location?: LatLng;
  }>;
}
